import SharedWorker from '@okikio/sharedworker';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { isDev } from '~/env/other';
import { trpc } from '~/utils/trpc';
import type {
  SignalConnectionState,
  SignalStatus,
  SignalTopicStatus,
  SignalWorkerStatus,
  WorkerOutgoingMessage,
} from './types';

export type TopicStatusEvent = Omit<SignalTopicStatus, 'type'>;
export type TopicStatusHandler = (event: TopicStatusEvent) => void;
const TOPIC_STATUS_EVENT = '__topicStatus';
import { Deferred, EventEmitter, teardownSignalWorker } from './utils';

export type SignalWorker = NonNullable<ReturnType<typeof useSignalsWorker>>;

const logs: Record<string, boolean> = {};
let logConnectionState = false;
let pendingDumpResolve: ((status: SignalWorkerStatus) => void) | null = null;

export function useSignalsWorker(options?: {
  onStateChange?: (args: SignalConnectionState) => void;
}) {
  const currentUser = useCurrentUser();
  const userId = currentUser?.id;
  const { onStateChange } = options ?? {};

  const [connection, setConnection] = useState<SignalStatus>();
  const [ready, setReady] = useState(false);
  const [worker, setWorker] = useState<SharedWorker | null>(null);
  const [reconnectCount, setReconnectCount] = useState(0);
  const shouldInitialize = connection === 'closed';

  const queryUtils = trpc.useUtils();
  const { data } = trpc.signals.getToken.useQuery(undefined, {
    enabled: !!userId && shouldInitialize,
    retry: isDev ? false : 3,
  });
  const accessToken = data?.accessToken;

  const emitterRef = useRef(new EventEmitter());
  const deferredRef = useRef(new Deferred());

  // handle init worker
  useEffect(() => {
    const emitter = emitterRef.current;
    // Built by `pnpm build:workers` (scripts/build-workers.mjs) → public/workers.
    // Static path (not new URL(import.meta.url)) to bypass Turbopack's broken
    // .ts SharedWorker compilation — see vercel/next.js#74842.
    const newWorker = new SharedWorker('/workers/signals.worker.js', {
      name: 'civitai-signals:2.1',
    });
    setReady(false);
    setWorker(newWorker);

    function cleanup() {
      // `teardownSignalWorker` sends `beforeunload` then uses the wrapper's
      // polymorphic close() — closing the MessagePort on SharedWorker-capable
      // browsers and terminating the dedicated Worker on the fallback path
      // (Android Chrome / Samsung Internet). Calling `newWorker.port.close()`
      // directly threw `TypeError: port.close is not a function` on the fallback
      // cohort, where `port` is the Worker (no `.close()`).
      teardownSignalWorker(newWorker);
      emitter.stop();
    }

    window.addEventListener('beforeunload', cleanup);
    return () => {
      window.removeEventListener('beforeunload', cleanup);
      cleanup();
      setWorker(null);
    };
  }, []);

  // handle register worker events
  useEffect(() => {
    if (!worker) return;

    worker.port.onmessage = async ({ data }: { data: WorkerOutgoingMessage }) => {
      if (data.type === 'worker:ready') setReady(true);
      else if (data.type === 'event:received') emitterRef.current.emit(data.target, data.payload);
      else if (data.type === 'pong') deferredRef.current.resolve();
      else if (data.type === 'topic:status') {
        const { type: _t, ...event } = data;
        emitterRef.current.emit(TOPIC_STATUS_EVENT, event);
      } else if (data.type === 'debug:dump') {
        if (pendingDumpResolve) {
          pendingDumpResolve(data.data);
          pendingDumpResolve = null;
        }
      } else if (data.type === 'connection:state') {
        setConnection(data.state ?? 'closed');
        onStateChange?.({ state: data.state, message: data.message });
        if (data.state === 'closed') {
          queryUtils.signals.getToken.invalidate();
          setReconnectCount((c) => c + 1);
        }
        if (logConnectionState) console.log({ state: data.state }, new Date().toLocaleTimeString());
      }
    };
  }, [worker]);

  // init
  useEffect(() => {
    if (worker && ready && accessToken && userId)
      worker.port.postMessage({
        type: 'connection:init',
        token: accessToken,
        userId,
      });
  }, [worker, accessToken, ready, userId, reconnectCount]);

  // ping
  useEffect(() => {
    function handleVisibilityChange() {
      if (document.visibilityState !== 'visible' || !worker) return;
      deferredRef.current = new Deferred();
      worker?.port.postMessage({ type: 'ping' });
      const timeout = setTimeout(() => deferredRef.current.reject(), 1000);
      deferredRef.current.promise
        .then(() => {
          clearTimeout(timeout);
          setReady(true);
        })
        .catch(() => {
          setReady(false);
          setConnection('closed');
        });
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [worker]);

  const workerMethods = useMemo(() => {
    function send(target: string, args: Record<string, unknown>) {
      worker?.port.postMessage({ type: 'send', target, args });
    }

    function on(target: string, cb: (data: unknown) => void) {
      worker?.port.postMessage({ type: 'event:register', target });
      emitterRef.current.on(target, cb);
    }

    function off(target: string, cb: (data: unknown) => void) {
      emitterRef.current.off(target, cb);
    }

    function topicRegister(topic: string, notify?: boolean) {
      worker?.port.postMessage({ type: notify ? 'topic:registerNotify' : 'topic:register', topic });
    }

    function topicUnsubscribe(topic: string) {
      worker?.port.postMessage({ type: 'topic:unsubscribe', topic });
    }

    function onTopicStatus(cb: TopicStatusHandler) {
      emitterRef.current.on(TOPIC_STATUS_EVENT, cb as (data: unknown) => void);
    }

    function offTopicStatus(cb: TopicStatusHandler) {
      emitterRef.current.off(TOPIC_STATUS_EVENT, cb as (data: unknown) => void);
    }

    return {
      on,
      off,
      send,
      topicRegister,
      topicUnsubscribe,
      onTopicStatus,
      offTopicStatus,
    };
  }, [worker]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.logSignal = (target, selector) => {
        function logFn(args: unknown) {
          if (selector) {
            const result = [args].find(selector);
            if (result) console.log(result);
          } else console.log(args);
        }

        if (!logs[target]) {
          logs[target] = true;
          workerMethods.on(target, logFn);
          console.log(`begin logging: ${target}`);
        }
      };

      window.ping = () => {
        worker?.port.postMessage({ type: 'ping' });
        logConnectionState = true;
      };

      window.signalsSubscribers = () => {
        const cbs = emitterRef.current.callbacks as Record<string, unknown[]>;
        console.table(
          Object.entries(cbs).map(([target, list]) => ({ target, count: list?.length ?? 0 }))
        );
      };

      window.signalsDump = () => {
        if (!worker) {
          console.log('[signals] No worker available');
          return;
        }
        pendingDumpResolve = (status) => {
          const { logEntries, recentSignals, ...summary } = status;
          console.log('[signals] Status:', summary);
          console.log(
            `[signals] Last event: ${
              status.lastEventReceivedAt
                ? `${Math.round((Date.now() - status.lastEventReceivedAt) / 1000)}s ago`
                : 'never'
            }`
          );
          console.log(
            `[signals] Last server pong: ${
              status.lastServerPongAt
                ? `${Math.round((Date.now() - status.lastServerPongAt) / 1000)}s ago`
                : 'never'
            }`
          );
          console.log(`[signals] Uptime: ${Math.round(status.uptime / 1000)}s`);
          console.table(
            logEntries.slice(-50).map((e) => ({
              time: new Date(e.ts).toLocaleTimeString(),
              type: e.type,
              detail: e.detail ?? '',
            }))
          );
          if (recentSignals.length) {
            console.groupCollapsed(`[signals] Recent signals (${recentSignals.length})`);
            for (const { ts, target, payload } of recentSignals) {
              console.log(`${new Date(ts).toLocaleTimeString()} ${target}`, payload);
            }
            console.groupEnd();
          } else {
            console.log('[signals] No signals received yet');
          }
        };
        worker.port.postMessage({ type: 'debug:dump' });
      };

      window.signalsStatus = () => {
        if (!worker) {
          console.log('[signals] No worker available');
          return;
        }
        pendingDumpResolve = (status) => {
          const { logEntries, ...summary } = status;
          console.log('[signals] Status:', summary);
          console.log(
            `[signals] Last event: ${
              status.lastEventReceivedAt
                ? `${Math.round((Date.now() - status.lastEventReceivedAt) / 1000)}s ago`
                : 'never'
            }`
          );
          console.log(
            `[signals] Last server pong: ${
              status.lastServerPongAt
                ? `${Math.round((Date.now() - status.lastServerPongAt) / 1000)}s ago`
                : 'never'
            }`
          );
        };
        worker.port.postMessage({ type: 'debug:dump' });
      };

      window.signalsVerbose = () => {
        worker?.port.postMessage({ type: 'debug:toggle-verbose' });
        console.log('[signals] Toggled verbose logging in worker');
      };
    }
  }, [workerMethods]);

  // const connected = state?.status === 'connected' || state?.status === 'reconnected';
  // console.log({ connected, status: state?.status });
  return workerMethods;
}
